import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import io from 'socket.io-client';
import { Consola } from 'consola';

import router from '@/router';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { Doctor } from '@/plugins/podolib/services/doctor';
import { CommandName } from '@/plugins/podolib/services/entities/EntityCommands';
import { StateStatus } from '@/plugins/podolib/services/analytics';

export type TOptions = {
  url: string;
  transports?: string[];
  path?: string;
};

export type ISocketFlags = {
  connected: boolean;
  authenticated: boolean;
  pending: boolean;
  timeout: number | null;
  error: Error | null;
};

export type TCommandData = {
  [key: string]: string | string[] | object | boolean | null | undefined;
  serviceName?: string;
  stationId?: string;
  patientId?: string;
  acquisitionId?: string;
  solesId?: string;
};

export type TQuery = {
  ressource: string;
  identifier?: object;
  params?: object;
  data?: object;
  additionnalQuery?: object;
};

export interface ISocketRepository extends ISocketFlags {
  session: SocketIOClient.Socket | undefined;

  connect(): Promise<void>;
  disconnect(): void;
}

export default class SocketRepository<RootState> implements ISocketRepository {
  // Option and references from module
  private readonly Vue: typeof _Vue;
  private readonly store: Store<any>;
  private readonly logger: Consola;
  private readonly options: TOptions;
  // Session
  private _session: SocketIOClient.Socket | undefined;
  // Flags
  private _connected: boolean;
  private _authenticated: boolean;
  private _pending: boolean;
  private _timeout: number | null;
  private _error: Error | null;

  /**
   * @param Vue
   * @param store
   * @param logger
   * @param options
   */
  constructor(Vue: typeof _Vue, store: Store<any>, logger: Consola, options: TOptions) {
    this.Vue = Vue;
    this.store = store;
    this.logger = logger.withTag('socket');
    this.options = options;
    // Default values
    this._connected = this.defaultValues.connected;
    this._authenticated = this.defaultValues.authenticated;
    this._pending = this.defaultValues.pending;
    this._timeout = this.defaultValues.timeout;
    this._error = this.defaultValues.error;
  }

  /**
   * Get default state
   */
  public get defaultValues(): ISocketFlags {
    return {
      connected: false,
      authenticated: false,
      pending: false,
      timeout: null,
      error: null,
    };
  }

  /**
   * Initiate connection wih the cloud
   */
  public async connect(): Promise<void> {
    // Send global event
    this.Vue.prototype.$pdc.bus.emitDebug('repository:connection-pending');

    let timeoutId: any;

    // Timeout
    const timeout = () =>
      new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          // Emit
          this.Vue.prototype.$pdc.bus.emitDebug('repository:connection-timeout');
          // Error
          const error = new Error('The attempt to connect to the cloud has timed out');
          this._error = error;
          this.logger.error(error);
          this.disconnect();
          reject(error);
        }, 10000); // -> 10sec
      });

    const _connect = () =>
      new Promise((resolve, reject) => {
        // Check if a session already exist
        if (this.session) {
          const error = new Error('A session already exist');
          this._error = error;
          this.logger.warn(error);
          reject(error);
          return;
        }
        // Log
        this.logger.info('Initialize connection...');
        // Up "_pending" flag
        this._pending = true;

        // Instantiate socket
        this._session = io(this.options.url, {
          transports: this.options.transports || ['polling', 'websocket'],
          path: this.options.path || '/socket.io/',
          reconnection: true,
        });

        // Create listeners
        this.createListeners();

        // Wait "connected" event
        this.Vue.prototype.$pdc.bus.once('repository:connected', resolve);
      });

    const _rejected = () =>
      new Promise((resolve, reject) => {
        const t = setTimeout(resolve, 1000);
        this.Vue.prototype.$pdc.bus.once('repository:disconnected', (reason: string) => {
          if (reason === 'io server disconnect') {
            clearTimeout(t);
            reject(new Error('Server rejected your connection request'));
          }
        });
      });

    try {
      await Promise.race([_connect(), timeout()]);
      await _rejected();
    } catch (e) {
      this.disconnect();
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }

    await this.authenticate();

    await this.sync();
  }

  public async authenticate(): Promise<void> {
    // Send global event
    this.Vue.prototype.$pdc.bus.emitDebug('repository:authentication-pending');

    let timeoutId: any;

    // Timeout
    const timeout = () =>
      new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          // Emit
          this.Vue.prototype.$pdc.bus.emitDebug('repository:authentication-timeout');
          // Error
          const error = new Error('The attempt to authenticate to the cloud has timed out');
          this._error = error;
          this.logger.error(error);
          this.disconnect();
          reject(error);
        }, 10000); // -> 10sec
      });

    const _authenticate = () =>
      new Promise<void>((resolve, reject) => {
        this._session!.emit(
          'authenticate',
          { jwt: this.Vue.prototype.$pdc.cognito.accessToken },
          (err: Error | null) => {
            if (err) {
              reject(err);
            } else {
              // Log
              this.logger.success('🔒 Authenticated');
              // Set flags
              this._authenticated = true;
              this._pending = this.defaultValues.pending;
              this._timeout = this.defaultValues.timeout;
              this._error = this.defaultValues.error;
              // Emit
              this.Vue.prototype.$pdc.bus.emitDebug('repository:authenticated');
              resolve();
            }
          },
        );
      });

    try {
      await Promise.race([_authenticate(), timeout()]);
    } catch (e) {
      this.disconnect();
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async sync() {
    try {
      console.log('> -------------------------------');
      // Doctor
      await this.Vue.prototype.$pdc.doctor.fetch();
      console.log('> -------------------------------');
      // Entities
      await this.Vue.prototype.$pdc.entities.sync();
      console.log('> -------------------------------');
      // Start analysis service
      await this.Vue.prototype.$pdc.analysis.start();
      console.log('> -------------------------------');
      // Addons
      /*await this.Vue.prototype.$pdc.addons.sync();
      console.log('> -------------------------------');*/
      this.Vue.prototype.$pdc.bus.emitDebug('app:ready');
      this.logger.success('🚀 App is ready');
      console.log('> -------------------------------');
    } catch (e) {
      if (e.message === 'NotDoctorProfile') {
        this.Vue.prototype.$pdc.bus.emitDebug(`doctor:not-exist`);
      }
      // Throw
      throw e;
    }
  }

  /**
   * Close connection wih the cloud
   */
  public disconnect(): void {
    // Check if a session exist
    if (!this._session) {
      this.logger.error('Session not exist');
      return;
    }
    this.logger.log('Disconnect');
    this._session.disconnect();
    this._session.removeAllListeners();
    delete this._session;
  }

  /**
   * Create socket listeners
   */
  private createListeners() {
    // CHeck if a session exist
    if (!this._session) {
      this.logger.error('Session not exist');
      return;
    }
    // Fired upon a connection including a successful reconnection.
    this._session.on('connect', async () => {
      // Log
      this.logger.success('🌐 Connected with the cloud');
      // Set flags
      this._connected = true;
      // Emit
      this.Vue.prototype.$pdc.bus.emitDebug('repository:connected');
    });

    // Fired upon a reconnection
    this._session.on('reconnect', async () => {
      try {
        await this.authenticate();
      } catch (e) {
        await this.Vue.prototype.$pdc.cognito!.signOut();
        await router.push('/auth/sign-in');
      }
    });

    // Fired upon a disconnection.
    this._session.on('disconnect', async (reason: any) => {
      // Log
      this.logger.warn('Disconnected from the cloud for this reason "%s"', reason);

      // Set flags
      this._connected = this.defaultValues.connected;
      this._authenticated = this.defaultValues.authenticated;

      // TODO : can be optimized
      switch (reason) {
        case 'forced close':
        case 'io client disconnect':
          this.Vue.prototype.$pdc.bus.emitDebug('repository:disconnected', reason);
          return;
        case 'io server disconnect':
          this.Vue.prototype.$pdc.bus.emitDebug('repository:disconnected', reason);
          return;
        default:
          this.Vue.prototype.$pdc.bus.emitDebug('repository:disconnected', reason);
          return;
      }
    });

    // Fired upon a connection error.
    this._session.on('connect_error', (error: Error) => {
      // Log
      this.logger.error('The connection with the cloud cannot be established');
      // Set flags
      this._error = error;
      // Emit
      this.Vue.prototype.$pdc.bus.emitDebug('repository:error');
    });

    // Fired upon a connection timeout.
    this._session.on('connect_timeout', (timeout: number) => {
      // Log
      this.logger.error(`The connection with the cloud cannot has timeout [${timeout}]`);
      // Set flags
      this._timeout = timeout;
      // Emit
      this.Vue.prototype.$pdc.bus.emitDebug('repository:timeout');
    });

    if (['local', 'develop'].includes(process.env.VUE_APP_BRANCH ?? 'local')) {
      [
        'stations created',
        'stations patched',
        'stations removed',
        'soles created',
        'soles patched',
        'soles removed',
        'patients created',
        'patients patched',
        'patients removed',
        'commands created',
        'commands patched',
        'commands removed',
        'doctors created',
        'doctors patched',
        'doctors removed',
      ].forEach((eventEName) =>
        this._session!.on(eventEName, () => {
          this.logger.log(`🎺 >>>>>>>> Event "${eventEName}"`);
        }),
      );
    }

    // Create entities listeners
    Object.values(this.Vue.prototype.$pdc.entities.entities).forEach((x: any) => {
      x.startListener();
    });

    // Create doctor listener
    this.Vue.prototype.$pdc.doctor.startListener();
  }

  // ---------------------------------------------------------------------------
  // Emitters

  /**
   * Send something to the cloud
   * @param type
   * @param service
   * @param args
   */
  public async emit(type: string, service: string, ...args: any): Promise<any> {
    // Check if a session exist
    if (!this._session) throw new Error('Not connected with the server');

    // Check user ...
    try {
      await this.Vue.prototype.$pdc.cognito.getUser();
    } catch (e) {
      await this.Vue.prototype.$pdc.cognito!.signOut();
      await router.push('/auth/sign-in');
      throw e;
    }

    const _emit = () =>
      new Promise<void>((resolve, reject) => {
        this._session!.emit(type, service, ...args, (err: Error | null, result: any) => {
          if (err) reject(err);
          else resolve(result);
        });
      });

    // Process
    try {
      if (!this._connected) {
        let timeoutId: any;

        // Timeout
        const timeout = () =>
          new Promise((resolve, reject) => {
            timeoutId = setTimeout(() => {
              // Emit
              this.Vue.prototype.$pdc.bus.emitDebug('repository:emit-timeout');
              // Error
              const error = new Error('The attempt to emit to the cloud has timed out');
              this._error = error;
              this.logger.error(error);
              reject(error);
            }, 5000); // -> 5sec
          });

        const _waitReconnectionAndAuthentication = () =>
          new Promise<void>((resolve) => {
            this.Vue.prototype.$pdc.bus.once('repository:authenticated', resolve);
            // Avoid memory leak
            setTimeout(() => {
              this.Vue.prototype.$pdc.bus.off('repository:authenticated', resolve);
            }, 5000); // -> 5sec
          });

        try {
          await Promise.race([_waitReconnectionAndAuthentication(), timeout()]);
        } finally {
          clearTimeout(timeoutId);
        }
      }

      // Emit
      return await _emit();
    } catch (e) {
      if (e === 'not authorized') {
        // Check user ...
        await this.Vue.prototype.$pdc.cognito.getUser();
        // Handshake
        await this.authenticate();
        // Retry emit
        return await _emit();
      } else {
        throw e;
      }
    }
  }

  /**
   * Create a command and send it
   * @param command
   * @param data
   */
  public emitCommand<E>(command: string, data: TCommandData): Promise<E> {
    return this.emit('create', 'commands', {
      command,
      ...data,
    });
  }

  /**
   * Delete a command with its id
   * @param commandId
   */
  public deleteCommand(commandId: string): Promise<void> {
    return this.emit('remove', 'commands', { _id: commandId });
  }

  /**
   * Delete commands with their ids
   * @param commandIds
   */
  public deleteCommands(commandIds: string[]): Promise<any> {
    return Promise.all(commandIds.map((commandId) => this.emit('remove', 'commands', { _id: commandId })));
  }

  /**
   * Delete all doctor's commands
   */
  public async deleteAllCommands(doctorId: TEntity<Doctor>['_id']): Promise<void> {
    return this.emit('remove', 'commands', null, {
      command: { $in: this.Vue.prototype.$pdc.helper.enumToArray(CommandName).map((value: any) => value.value) },
      userId: doctorId,
    });
  }

  /**
   * Fetch a ressource
   * @param ressource
   * @param params
   */
  public fetchRessource({ ressource, params = {} }: TQuery): Promise<void> {
    return this.emit('find', ressource, params);
  }

  /**
   * Get a ressource
   * @param ressource
   * @param identifier
   * @param params
   */
  public getRessource({
    ressource,
    identifier,
    params,
  }: {
    ressource: string;
    identifier: object;
    params: object;
  }): Promise<void> {
    return this.emit('get', ressource, identifier, params);
  }

  /**
   * Patch a ressource
   * @param ressource
   * @param params
   * @param data
   * @param additionnalQuery
   */
  public patchRessource({ ressource, params = {}, data = {}, additionnalQuery = {} }: TQuery) {
    return this.emit('patch', ressource, params, data, additionnalQuery);
  }

  /**
   * Get a ressource
   * @param ressource
   * @param identifier
   * @param params
   */
  public deleteRessource({ ressource, identifier, params = {} }: TQuery & { params: { _id?: string } }): Promise<void> {
    return this.emit('remove', ressource, identifier, params);
  }

  // ---------------------------------------------------------------------------
  // Getters

  public get error(): Error | null {
    return this._error;
  }

  public get timeout(): number | null {
    return this._timeout;
  }

  public get pending(): boolean {
    return this._pending;
  }

  public get connected(): boolean {
    return this._connected;
  }

  public get authenticated(): boolean {
    return this._authenticated;
  }

  public get session(): SocketIOClient.Socket | undefined {
    return this._session;
  }
}
